import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface LegalDocument {
  id: string
  title: string
  document_type: string
  status: string
  party_name: string
  effective_date: string
  expiry_date: string | null
  owner: string
}

interface ComplianceItem {
  id: string
  requirement: string
  regulation: string
  status: string
  due_date: string
  assigned_to: string
  risk_level: string
}

export default function LegalPage() {
  const [documents, setDocuments] = useState<LegalDocument[]>([])
  const [compliance, setCompliance] = useState<ComplianceItem[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [docsRes, compRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/legal/documents'),
      fetch(API_BASE + '/legal/compliance'),
      fetch(API_BASE + '/legal/statistics')
    ])
    if (docsRes.ok) setDocuments((await docsRes.json()).documents || [])
    if (compRes.ok) setCompliance((await compRes.json()).compliance_items || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'compliant': case 'approved': return 'bg-green-100 text-green-800'
      case 'pending': case 'under_review': case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'expired': case 'non_compliant': case 'overdue': return 'bg-red-100 text-red-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  const getDocTypeIcon = (type: string) => {
    switch (type) {
      case 'contract': return '📄'
      case 'nda': return '🔒'
      case 'agreement': return '🤝'
      case 'policy': return '📋'
      case 'license': return '📜'
      default: return '📁'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Legal & Compliance</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Document</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add Compliance</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Documents</div>
              <div className="text-2xl font-bold">{statistics.total_documents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_documents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expiring Soon</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.expiring_soon}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Compliance Rate</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.compliance_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Review</div>
              <div className="text-2xl font-bold">{statistics.pending_review}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Legal Documents</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {documents.slice(0, 10).map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span>{getDocTypeIcon(doc.document_type)}</span>
                        <div>
                          <div className="font-medium">{doc.title}</div>
                          <div className="text-xs text-gray-500">{doc.document_type} • {doc.owner}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{doc.party_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs">Effective: {doc.effective_date}</div>
                      {doc.expiry_date && (
                        <div className="text-xs text-gray-500">Expires: {doc.expiry_date}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(doc.status)}`}>
                        {doc.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Compliance Tracker</h3>
            <div className="space-y-3">
              {compliance.slice(0, 10).map(item => (
                <div key={item.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{item.requirement}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(item.status)}`}>
                      {item.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{item.regulation}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{item.assigned_to}</span>
                    <span className={`text-xs font-medium ${getRiskColor(item.risk_level)}`}>
                      {item.risk_level} risk
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Due: {item.due_date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
